import { trackGoal, trackPageview } from '@/utils/tracker';

/**
 * Яндекс.Метрика. Счётчик подключается только когда администратор указал его
 * номер в настройках, поэтому сайт без номера не грузит чужие скрипты.
 *
 * Цели считаются по именам ниже; те же имена нужно завести в интерфейсе Метрики.
 * Параллельно цели и просмотры пишет собственный счётчик (utils/tracker) —
 * он работает, даже когда Метрика не загрузилась.
 */
export const METRIKA_GOALS = {
  lead: 'lead_listing',
  inquiry: 'inquiry_general',
  producer: 'inquiry_producer',
  phoneClick: 'click_phone',
  messengerClick: 'click_messenger',
  register: 'register',
} as const;

export type MetrikaGoal = (typeof METRIKA_GOALS)[keyof typeof METRIKA_GOALS];

type MetrikaFn = (id: number, action: string, ...args: unknown[]) => void;

declare global {
  interface Window {
    ym?: MetrikaFn;
  }
}

const SCRIPT_SRC = 'https://mc.yandex.ru/metrika/tag.js';

let activeCounterId: number | null = null;

export function installMetrika(counterId: string | null, doc: Document = document): boolean {
  const id = Number(counterId);
  if (!counterId || !Number.isInteger(id) || id <= 0 || activeCounterId === id) return false;

  activeCounterId = id;

  // Очередь вызовов до загрузки скрипта — как в официальном сниппете, только
  // без inline-кода: CSP сайта запрещает встроенные скрипты.
  const win = doc.defaultView as (Window & { ym?: MetrikaFn }) | null;
  if (!win) return false;
  const queue: unknown[][] = [];
  const stub = ((...args: unknown[]) => {
    queue.push(args);
  }) as MetrikaFn & { a?: unknown[][]; l?: number };
  stub.a = queue;
  stub.l = Date.now();
  win.ym ??= stub;

  const script = doc.createElement('script');
  script.async = true;
  script.src = SCRIPT_SRC;
  doc.head.appendChild(script);

  win.ym(id, 'init', {
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
  });

  return true;
}

/** Достигнутая цель: в свой счётчик всегда, в Метрику — если она подключена. */
export function reachGoal(goal: MetrikaGoal, params?: Record<string, unknown>): void {
  trackGoal(goal, params);
  if (activeCounterId === null || typeof window === 'undefined' || !window.ym) return;
  window.ym(activeCounterId, 'reachGoal', goal, params);
}

/** Смена страницы в SPA — Метрика сама переходы не видит. */
export function trackPageView(url: string): void {
  trackPageview(url);
  if (activeCounterId === null || typeof window === 'undefined' || !window.ym) return;
  window.ym(activeCounterId, 'hit', url);
}

/** Только для тестов. */
export function resetMetrika(): void {
  activeCounterId = null;
}
